"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "@/lib/utils";
import { phCapture } from "@/utils/analytics";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { ComparisonOrderDialog, type OptimizerComparisonOrderRuntime } from "@/components/optimizer/comparison-order-dialog";
import { WeightsDialog, type WhalesRow } from "@/components/weights-dialog";
import { useWhalesUpsell } from "@/components/odds/whales-upsell";
import { WhalesLogoMark } from "@/components/odds/whales";
import { comparisonColumnLabel, WHALES_COLUMN, WHALES_LOCKED_LABEL, type ComparisonColumnId } from "@/lib/whales";
import { DIAMOND_PILL_CLASS } from "@/components/billing/diamond-tags";
import { pricedChoices, whalesPriceOn } from "@/lib/fantasy-props/rows";
import { orderOptimizerAnalyticalBooks } from "@/components/optimizer/sportsbook-field-groups";
import { getActiveAdvancedFilterCount } from "@/components/optimizer/filters/advanced-filter-count";
import { clampOddsRangeUpdate } from "@/components/optimizer/filters/drawer-controls";
import {
  clearedSheetUpdate,
  commitFilterSheet,
  diffExtraQuery,
  extraQueryEquals,
  hasSliceKeys,
  openFilterSheet,
  patchSheetExtra,
  pickExtraQuery,
  sheetStateForForm,
  splitSheetUpdate,
  type FilterSheetDrafts,
} from "@/components/optimizer/filters/extra-query";
import { oddsRangeActive, orderOddsRange } from "@/components/optimizer/odds-range";
import { isFantasyPropsBook } from "@/components/optimizer/props-books";
import { getSelectableSlipTypes, isExplicitSlipTypePlatform } from "@/components/optimizer/props-slip-breakevens";
import { canonicalComparisonBooks, queryStateFromSearch, serializeQueryState, type FilterChangeOptions, type MarketScope, type OptimizerQueryState, type OverUnder } from "@/components/optimizer/query-state";
import { ToolViewsControl } from "@/components/tools/tool-toolbar";
import type { SavedViewToolKey } from "@/lib/saved-views";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import { useAccordionMemory } from "@/hooks/use-accordion-memory";
import type { UserWeightPreset } from "@/lib/actions/weight-presets";
import type { MobileView } from "@/lib/saved-filter-pages";
import { OFFICIAL_PRESETS, type BookWeights } from "@/lib/weights-config";
import type { OddsFormat } from "@/utils/calculators/odds-utils";
import { CrumbMenu, type CrumbItem } from "./crumb-menu";
import { chipClass, DualRange, FieldRow, FilterSheetShell, MenuRow, NumberField, OddsField, SearchInput, SectionLabel, Segmented, Stepper, Switch } from "./chrome";
import type { TimeChoices } from "@/lib/game-date";
import { QuickFilters } from "./quick-filters";
import { StartTimeSection } from "./start-time";

export type FantasyFiltersSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: OptimizerQueryState;
  onChange: (update: Partial<OptimizerQueryState>, options?: FilterChangeOptions) => void;
  onReset?: () => void;
  times: TimeChoices;
  liveChoice?: boolean;
  columns: readonly PlatformType[];
  analyticalBookIds: readonly PlatformType[];
  showWeightsFilter: boolean;
  diamond: boolean;
  ownBook: PlatformType | null;
  picked: Pick<OptimizerQueryState, "sportsbook" | "platform" | "slipType"> | null;
  userPresets: UserWeightPreset[];
  onSavePreset: (name: string, weights: BookWeights, requiredBooks: string[]) => Promise<void>;
  onDeletePreset: (name: string) => Promise<void>;
  onSetDefault: (name: string | null) => Promise<void>;
  markets: readonly string[];
  gameMarkets?: readonly string[];
  showMinEV?: boolean;
  minEVFloor?: number;
  showMinLiquidity?: boolean;
  matchups: readonly string[];
  oddsFormat: OddsFormat;
  onOddsFormatChange: (format: OddsFormat) => void;
  comparisonOrderRuntime?: OptimizerComparisonOrderRuntime;
  isMobile: boolean;
  layout?: { readonly value: MobileView; readonly onChange: (view: MobileView) => void };
  views?: { readonly toolKey: SavedViewToolKey; readonly userId?: string | null };
};

const LAYOUTS: readonly { readonly id: MobileView; readonly label: string }[] = [
  { id: "cards", label: "Cards" },
  { id: "table", label: "Table" },
];

const ODDS_FORMATS: readonly { readonly id: OddsFormat; readonly label: string; readonly ariaLabel: string }[] = [
  { id: "american", label: "+100", ariaLabel: "American" },
  { id: "decimal", label: "Dec.", ariaLabel: "Decimal" },
  { id: "cents", label: "%", ariaLabel: "Percent" },
];

const HIT_CHIPS = [
  { label: "Under 35%", min: 0, max: 35 },
  { label: "Over 60%", min: 60, max: 100 },
] as const;

const MARKET_SCOPES: readonly { readonly id: MarketScope; readonly label: string }[] = [
  { id: "", label: "All" },
  { id: "player", label: "Player Props" },
  { id: "game", label: "Game Lines" },
];

const OVER_UNDER: readonly { readonly id: OverUnder; readonly label: string }[] = [
  { id: "", label: "All" },
  { id: "over", label: "Over" },
  { id: "under", label: "Under" },
];

function weightsMatch(a: BookWeights | null, b: BookWeights | null): boolean {
  if (a === null || b === null) return a === b;
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  return aKeys.length === bKeys.length && aKeys.every((key, index) => key === bKeys[index] && a[key] === b[key]);
}

function weightsLabel(state: OptimizerQueryState, userPresets: readonly UserWeightPreset[]): string {
  if (state.preset) return state.preset;
  if (state.weights !== null) {
    const match = userPresets.find((preset) => weightsMatch(preset.weights, state.weights))
      ?? OFFICIAL_PRESETS.find((preset) => weightsMatch(preset.weights, state.weights));
    if (match) return "name" in match ? match.name : match.label;
    const count = Object.keys(state.weights).length;
    return `${count} ${count === 1 ? "book" : "books"}`;
  }
  return state.requiredBooks.length > 0 ? `Weights · ${state.requiredBooks.length} req` : "Weights";
}

function requiredComparisonBooksLabel(books: readonly ComparisonColumnId[]): string {
  if (books.length === 0) return "Any book";
  return books.length === 1 ? comparisonColumnLabel(books[0]) : `${comparisonColumnLabel(books[0])} +${books.length - 1}`;
}

function ComparisonColumnMark({ id }: { id: ComparisonColumnId }) {
  return id === WHALES_COLUMN ? <WhalesLogoMark size={16} /> : <SportsbookLogoMark sportsbook={id} size={16} decorative />;
}

function Chevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 text-zinc-500 transition-transform duration-200 motion-reduce:transition-none"><path d="m6 9 6 6 6-6" /></svg>
  );
}

function Section({
  id, title, summary, active, tall, children,
}: {
  id: string;
  title: string;
  summary: string;
  active: boolean;
  tall: boolean;
  children: React.ReactNode;
}) {
  return (
    <AccordionPrimitive.Item value={id} className="border-b border-surface-line">
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          className={cn(
            "data-num flex flex-1 items-center justify-between gap-3 px-3 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500 hover:text-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 data-[state=open]:text-zinc-300 [&[data-state=open]_svg]:rotate-180",
            tall ? "h-12" : "h-10",
          )}
        >
          <span>{title}</span>
          <span className="flex min-w-0 items-center gap-2">
            <span className={cn("truncate text-[11px] font-medium normal-case tracking-normal", active ? "text-primary" : "text-zinc-400")}>{summary}</span>
            <Chevron />
          </span>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down motion-reduce:animate-none">
        <div className="px-1 pb-2">{children}</div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
}

export function Checklist({
  title, options, selected, onChange, tall, placeholder,
}: {
  title: string;
  options: readonly string[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  tall: boolean;
  placeholder: string;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = q === "" ? options : options.filter((option) => option.toLowerCase().includes(q));
  const chosen = new Set(selected);
  const toggle = (option: string) => onChange(chosen.has(option) ? selected.filter((value) => value !== option) : [...selected, option]);
  const mine = new Set(options);
  const others = selected.filter((value) => !mine.has(value));
  const count = selected.length - others.length;
  return (
    <div role="group" aria-label={title}>
      <div className="flex items-center justify-between pr-1.5">
        <SectionLabel>{title}{count > 0 && <span className="ml-1.5 text-primary">{count}</span>}</SectionLabel>
        <div className="flex gap-2.5 pt-2 text-[11px] font-medium">
          <button type="button" onClick={() => onChange([...others, ...options])} className={cn("text-zinc-400 hover:text-foreground", tall && "px-1 py-2")}>All</button>
          <button type="button" onClick={() => onChange(others)} className={cn("text-zinc-400 hover:text-foreground", tall && "px-1 py-2")}>None</button>
        </div>
      </div>
      {options.length > 8 && <div className="px-1.5"><SearchInput value={query} onChange={setQuery} placeholder={placeholder} label={placeholder} /></div>}
      <div className="max-h-56 overflow-y-auto px-1 scrollbar-custom scrollbar-color-primary">
        {shown.length === 0 ? (
          <p className="px-2.5 py-2 text-[12px] text-zinc-500">Nothing matches.</p>
        ) : (
          shown.map((option) => (
            <MenuRow key={option} role="menuitemcheckbox" active={chosen.has(option)} onClick={() => toggle(option)} tall={tall}>{option}</MenuRow>
          ))
        )}
      </div>
    </div>
  );
}

export function FantasyFiltersSheet({
  open, onOpenChange, state, onChange, onReset, times, liveChoice = false, columns, analyticalBookIds, showWeightsFilter, diamond, ownBook, picked, userPresets, onSavePreset, onDeletePreset,
  onSetDefault, markets, gameMarkets, showMinEV = false, minEVFloor = 0, showMinLiquidity = false, matchups, oddsFormat, onOddsFormatChange, comparisonOrderRuntime, isMobile, layout, views,
}: FantasyFiltersSheetProps) {
  const [orderOpen, setOrderOpen] = useState(false);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [drafts, setDrafts] = useState<FilterSheetDrafts | null>(null);
  const [draftFloor, setDraftFloor] = useState(minEVFloor);
  if (draftFloor !== minEVFloor) {
    setDraftFloor(minEVFloor);
    setDrafts((current) => (current !== null && current.extra.minEV < minEVFloor ? patchSheetExtra(current, { minEV: state.minEV }) : current));
  }
  const provenance = useRef<{ source: "preset"; presetId: string } | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    setDrafts(open ? openFilterSheet("extra", stateRef.current) : null);
    if (!open) provenance.current = null;
  }, [open]);

  const tall = isMobile;
  const form = sheetStateForForm(drafts, state);
  const fairPriced = isFantasyPropsBook(form.platform);
  const whalesPriced = whalesPriceOn(form.platform);
  const weightsUpsell = useWhalesUpsell(whalesPriced);
  const requiredBooksUpsell = useWhalesUpsell(whalesPriced);
  const priced = pricedChoices(form, diamond);
  const dirty = drafts !== null && !extraQueryEquals(drafts.extra, pickExtraQuery(state));
  const activeCount = getActiveAdvancedFilterCount(priced, showWeightsFilter, true, showMinEV);
  const [expanded, setExpanded] = useAccordionMemory("optimizer:expandedSections", []);

  const change = (update: Partial<OptimizerQueryState>, options?: FilterChangeOptions) => {
    const { world, extra } = splitSheetUpdate(update);
    if (options?.source === "preset") {
      provenance.current = options.presetId ? { source: "preset", presetId: options.presetId } : null;
    } else if (hasSliceKeys(world) || hasSliceKeys(extra)) {
      provenance.current = null;
    }
    if (hasSliceKeys(world)) onChange(world, { immediate: true, source: options?.source ?? "sheet" });
    if (hasSliceKeys(extra)) setDrafts((current) => patchSheetExtra(current ?? openFilterSheet("extra", stateRef.current), extra));
  };

  const apply = () => {
    const next = commitFilterSheet(drafts ?? openFilterSheet("extra", state), state);
    const extra = diffExtraQuery(pickExtraQuery(next), pickExtraQuery(state));
    const changed = Object.keys(extra);
    phCapture("filters_applied", { page: "optimizer", section: "extra", changed_count: changed.length, changed_filters: changed });
    const from = provenance.current;
    provenance.current = null;
    if (from) phCapture("preset_applied", { page: "optimizer", preset: from.presetId });
    if (changed.length > 0) onChange(extra, { immediate: true, source: from?.source ?? "sheet" });
    onOpenChange(false);
  };

  const applyView = (search: string) => {
    const next = queryStateFromSearch(search);
    provenance.current = null;
    setDrafts(null);
    onChange(next, { immediate: true });
  };

  const comparisonBooks = useMemo(() => orderOptimizerAnalyticalBooks(columns, analyticalBookIds), [columns, analyticalBookIds]);
  const slipOptions = form.platform === picked?.platform && isExplicitSlipTypePlatform(form.platform) ? getSelectableSlipTypes(form.platform) : [];
  const minBooks = form.minDataPoints ?? 0;
  const minEV = showMinEV ? form.minEV ?? 0 : 0;
  const minLiquidity = showMinLiquidity ? form.minLiquidity ?? 0 : 0;
  const minHit = form.minOddsToHit ?? 0;
  const maxHit = form.maxOddsToHit ?? 100;
  const hitActive = minHit > 0 || maxHit < 100;
  const oddsActive = oddsRangeActive(form);
  const whales: WhalesRow = whalesPriced
    ? diamond ? { kind: "editable" } : { kind: "locked", unlock: () => weightsUpsell.unlock("weights"), upsell: weightsUpsell.upsell }
    : { kind: diamond ? "whenHeld" : "hidden" };
  const modelActive = priced.weights !== null || priced.requiredBooks.length > 0;
  const marketActive = form.statTypes.length > 0 || form.matchups.length > 0;
  const displayActive = form.hidePushProps || Boolean(comparisonOrderRuntime?.isCustom);

  const scopeLabel = MARKET_SCOPES.find((scope) => scope.id === form.marketScope)?.label.toLowerCase() ?? "all";
  const marketSummary = gameMarkets === undefined
    ? `${form.statTypes.length > 0 ? form.statTypes.length : "all"} stats · ${form.matchups.length > 0 ? form.matchups.length : "all"} matchups`
    : `${scopeLabel} · ${form.statTypes.length > 0 ? form.statTypes.length : "all"} markets · ${form.matchups.length > 0 ? form.matchups.length : "all"} matchups`;
  const displaySummary = [
    ODDS_FORMATS.find((option) => option.id === oddsFormat)?.label,
    form.hidePushProps ? "pushes hidden" : "pushes shown",
    comparisonOrderRuntime?.isCustom ? "custom columns" : null,
  ].filter(Boolean).join(" · ");

  const { requiredComparisonBooks } = form;
  const whalesRequired = requiredComparisonBooks.includes(WHALES_COLUMN);
  const whalesPickable = whales.kind === "editable" || (whales.kind === "whenHeld" && whalesRequired);
  const keptBooks = requiredComparisonBooks.filter((book) => book === WHALES_COLUMN && !whalesPickable);
  const whalesIcon = <WhalesLogoMark size={16} />;
  const whalesBooks: CrumbItem[] = whales.kind === "locked"
    ? [{ id: WHALES_COLUMN, label: "Whales", active: false, icon: whalesIcon, locked: { name: WHALES_LOCKED_LABEL, tag: <span className={DIAMOND_PILL_CLASS}>Diamond</span>, unlock: () => requiredBooksUpsell.unlock("required_books") } }]
    : whalesPickable
      ? [{ id: WHALES_COLUMN, label: "Whales", active: whalesRequired, multiple: true, icon: whalesIcon }]
      : [];
  const bookItems: CrumbItem[] = [
    { id: "", label: "Any book", active: requiredComparisonBooks.length === keptBooks.length },
    ...whalesBooks,
    ...comparisonBooks.map((book) => ({
      id: book, label: sportsbookIdToDisplay(book), active: requiredComparisonBooks.includes(book), multiple: true,
      icon: <SportsbookLogoMark sportsbook={book} size={16} decorative />,
    })),
  ];
  const pickRequiredBook = (id: string) => {
    const checked = requiredComparisonBooks.some((book) => book === id);
    change({ requiredComparisonBooks: id === "" ? keptBooks : canonicalComparisonBooks(checked ? requiredComparisonBooks.filter((book) => book !== id) : [...requiredComparisonBooks, id]) });
  };

  const body = (
    <>
      <StartTimeSection
        time={{ days: form.gameDays, within: form.startsWithin, live: form.live }}
        times={times}
        tall={tall}
        liveChoice={liveChoice}
        onTime={(next) => change({ gameDays: [...next.days], startsWithin: next.within, live: next.live }, { immediate: true })}
      />

      {slipOptions.length > 0 && (
        <section aria-label="Slip type">
          <SectionLabel>Slip type</SectionLabel>
          <div role="radiogroup" aria-label="Slip type" className="grid grid-cols-3 gap-1.5 px-1.5">
            {[{ id: "", label: "Best Available" }, ...slipOptions].map((option) => {
              const active = (form.slipType || "") === option.id;
              return (
                <button key={option.id} type="button" role="radio" aria-checked={active} onClick={() => change({ slipType: option.id }, { immediate: true })} className={cn(chipClass(active, tall), "h-auto min-h-8 whitespace-normal py-1.5 text-center leading-4", tall && "min-h-9")}>
                  {option.label}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {showWeightsFilter && (
        <section aria-label="Fair value" className="px-1 pt-2">
          <FieldRow
            label="Fair value"
            active={modelActive}
            tall={tall}
            below={priced.weights !== null && (
              <div className="mt-2.5 flex h-[22px] items-end gap-[3px]" aria-hidden="true">
                {Object.values(priced.weights).sort((a, b) => b - a).slice(0, 8).map((weight, index, all) => (
                  <div key={index} className="flex-1 rounded-[2px] bg-primary" style={{ height: `${Math.max(12, (weight / (all[0] || 1)) * 100)}%`, opacity: 0.5 - index * 0.05 }} />
                ))}
              </div>
            )}
          >
            <button
              type="button"
              onClick={() => setWeightsOpen(true)}
              className={cn(chipClass(modelActive, tall), "max-w-[180px]")}
            >
              <span className="truncate">{weightsLabel(priced, userPresets)}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 text-zinc-500"><path d="m9 18 6-6-6-6" /></svg>
            </button>
          </FieldRow>
        </section>
      )}

      <section aria-label="Thresholds" className="mt-2 border-t border-surface-line">
        <SectionLabel>Thresholds</SectionLabel>
        <div className="px-1">
          <FieldRow label="Min sportsbooks" active={minBooks > 0} tall={tall}>
            <Stepper value={minBooks} min={0} max={10} onChange={(value) => change({ minDataPoints: value })} label="minimum number of sportsbooks" active={minBooks > 0} />
          </FieldRow>
          <FieldRow label="Over / Under" active={form.overUnder !== ""} tall={tall}>
            <Segmented<OverUnder> label="Over / Under" value={form.overUnder} tall={tall} options={OVER_UNDER} onChange={(overUnder) => change({ overUnder })} />
          </FieldRow>
          <FieldRow
            label="Odds to hit"
            active={hitActive}
            tall={tall}
            below={(
              <div className="flex flex-col gap-2 pt-2">
                <DualRange min={0} max={100} low={minHit} high={maxHit} label="Odds to hit range" onCommit={({ low, high }) => change({ minOddsToHit: low, maxOddsToHit: high })} />
                <Segmented<string>
                  label="Odds to hit presets"
                  value={HIT_CHIPS.find((chip) => chip.min === minHit && chip.max === maxHit)?.label ?? ""}
                  options={HIT_CHIPS.map((chip) => ({ id: chip.label, label: chip.label }))}
                  onChange={(label) => {
                    const chip = HIT_CHIPS.find((candidate) => candidate.label === label);
                    if (chip) change({ minOddsToHit: chip.min, maxOddsToHit: chip.max });
                  }}
                />
              </div>
            )}
          >
            <NumberField value={minHit > 0 ? minHit : null} placeholder="0" min={0} max={100} label="Minimum odds to hit percentage" onCommit={(value) => change(clampOddsRangeUpdate({ minOddsToHit: value }, { min: minHit, max: maxHit }))} />
            <span className="text-[11px] text-zinc-500">to</span>
            <NumberField value={maxHit < 100 ? maxHit : null} placeholder="100" min={0} max={100} label="Maximum odds to hit percentage" onCommit={(value) => change(clampOddsRangeUpdate({ maxOddsToHit: value }, { min: minHit, max: maxHit }))} />
          </FieldRow>
          <FieldRow label="Required books" active={priced.requiredComparisonBooks.length > 0} tall={tall}>
            <CrumbMenu
              title="Required books"
              label={requiredComparisonBooksLabel(priced.requiredComparisonBooks)}
              icon={priced.requiredComparisonBooks[0] === undefined ? undefined : <ComparisonColumnMark id={priced.requiredComparisonBooks[0]} />}
              items={bookItems}
              isMobile={isMobile}
              header={<p className="px-2.5 py-2 text-[12px] text-zinc-500">Plays must be priced at every checked book.</p>}
              align="right"
              tone="quiet"
              onPick={pickRequiredBook}
            />
          </FieldRow>
          {showMinEV && (
            <FieldRow label="Min EV" active={minEV !== 0} tall={tall}>
              <NumberField value={minEV !== 0 ? minEV : null} placeholder="0" min={minEVFloor} max={100} step="any" label="Minimum EV percentage" onCommit={(value) => change({ minEV: value })} />
              <span className="text-[11px] text-zinc-500">%</span>
            </FieldRow>
          )}
          <FieldRow label={fairPriced ? "Fair odds" : "Odds"} active={oddsActive} tall={tall}>
            <OddsField value={form.minOdds} placeholder="Min" label="Minimum odds" onCommit={(value) => change(orderOddsRange(value, form.maxOdds))} />
            <span className="text-[11px] text-zinc-500">to</span>
            <OddsField value={form.maxOdds} placeholder="Max" label="Maximum odds" onCommit={(value) => change(orderOddsRange(form.minOdds, value))} />
          </FieldRow>
          {showMinLiquidity && (
            <FieldRow label="Min liquidity" active={minLiquidity > 0} tall={tall}>
              <span className="text-[12px] text-zinc-500">$</span>
              <NumberField value={minLiquidity > 0 ? minLiquidity : null} placeholder="0" min={0} max={1_000_000} label="Minimum liquidity in dollars" onCommit={(value) => change({ minLiquidity: Math.round(value) })} className="w-20 text-right" />
            </FieldRow>
          )}
        </div>
      </section>

      <AccordionPrimitive.Root type="multiple" value={expanded} onValueChange={setExpanded} className="mt-2 border-t border-surface-line">
        {(gameMarkets !== undefined || markets.length > 0 || matchups.length > 0) && (
          <Section id="market" title={gameMarkets === undefined ? "Stats & matchups" : "Markets & matchups"} summary={marketSummary} active={marketActive} tall={tall}>
            {gameMarkets !== undefined && (
              <div className="px-1.5 pt-2">
                <Segmented<MarketScope>
                  label="Markets"
                  value={form.marketScope}
                  tall={tall}
                  stretch
                  options={MARKET_SCOPES.map((scope) => ({ id: scope.id, label: scope.label }))}
                  onChange={(scope) => change({ marketScope: scope }, { immediate: true })}
                />
              </div>
            )}
            {gameMarkets !== undefined && gameMarkets.length > 0 && (
              <Checklist title="Game Lines" options={gameMarkets} selected={form.statTypes} onChange={(statTypes) => change({ statTypes })} tall={tall} placeholder="Search game lines" />
            )}
            {markets.length > 0 && (
              <Checklist title={gameMarkets === undefined ? "Stats" : "Player Props"} options={markets} selected={form.statTypes} onChange={(statTypes) => change({ statTypes })} tall={tall} placeholder={gameMarkets === undefined ? "Search stats" : "Search player props"} />
            )}
            {matchups.length > 0 && (
              <Checklist title="Matchups" options={matchups} selected={form.matchups} onChange={(next) => change({ matchups: next })} tall={tall} placeholder="Search matchups" />
            )}
          </Section>
        )}

        <Section id="display" title="Display" summary={displaySummary} active={displayActive} tall={tall}>
          <FieldRow label="Odds format" tall={tall}>
            <Segmented<OddsFormat> label="Odds format" value={oddsFormat} tall={tall} options={ODDS_FORMATS.map((option) => ({ id: option.id, label: option.label, ariaLabel: option.ariaLabel }))} onChange={onOddsFormatChange} />
          </FieldRow>
          {layout && (
            <FieldRow label="Layout" tall={tall}>
              <Segmented<MobileView> label="Layout" value={layout.value} tall={tall} options={LAYOUTS} onChange={layout.onChange} />
            </FieldRow>
          )}
          {comparisonOrderRuntime && (
            <FieldRow label="Comparison columns" active={comparisonOrderRuntime.isCustom} tall={tall}>
              <button type="button" disabled={!comparisonOrderRuntime.canWrite} title={comparisonOrderRuntime.canWrite ? undefined : "Preferences unavailable"} onClick={() => setOrderOpen(true)} className={cn(chipClass(comparisonOrderRuntime.isCustom, tall), "disabled:cursor-not-allowed disabled:opacity-40")}>
                Reorder
              </button>
            </FieldRow>
          )}
          <Switch checked={form.hidePushProps} onChange={(checked) => change({ hidePushProps: checked })} title="Hide push props" tall={tall} />
        </Section>
      </AccordionPrimitive.Root>

      <QuickFilters
        form={form}
        fields={{ minEV: showMinEV, minLiquidity: showMinLiquidity, model: showWeightsFilter }}
        ownBook={ownBook}
        userPresets={userPresets}
        tall={tall}
        onPick={(update, presetId) => change(update, { source: "preset", presetId })}
      />
      {whales.kind === "locked" && requiredBooksUpsell.upsell}
    </>
  );

  const dialogs = (
    <>
      {comparisonOrderRuntime && <ComparisonOrderDialog open={orderOpen} onOpenChange={setOrderOpen} runtime={comparisonOrderRuntime} />}
      {showWeightsFilter && (
        <WeightsDialog
          open={weightsOpen}
          onOpenChange={(next) => {
            setWeightsOpen(next);
            if (!next) weightsUpsell.close();
          }}
          value={form.weights}
          activePresetName={form.preset}
          requiredBooks={form.requiredBooks}
          onApply={(weights, preset, requiredBooks) => change({ weights, preset, requiredBooks })}
          userPresets={userPresets}
          onSavePreset={onSavePreset}
          onDeletePreset={onDeletePreset}
          onSetDefault={onSetDefault}
          availableBooks={analyticalBookIds}
          whales={whales}
          mobile={isMobile}
        />
      )}
    </>
  );

  return (
    <FilterSheetShell
      open={isMobile ? open && !orderOpen && !weightsOpen : open}
      onOpenChange={onOpenChange}
      isMobile={isMobile}
      activeCount={activeCount}
      dirty={dirty}
      onReset={() => {
        onReset?.();
        change(clearedSheetUpdate(state));
      }}
      onApply={apply}
      views={views && (
        <ToolViewsControl
          toolKey={views.toolKey}
          userId={views.userId}
          currentSearch={`?${serializeQueryState(picked === null ? form : { ...form, sportsbook: picked.sportsbook, platform: picked.platform, slipType: picked.slipType }).toString()}`}
          tall={tall}
          onApplySearch={applyView}
        />
      )}
      dialogs={dialogs}
    >
      {body}
    </FilterSheetShell>
  );
}
